import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  ApiKey,
  Automation,
  CalendarAccountView,
  StoredFile,
  EmailTemplate,
  MailAccountView,
  MailIgnoreRule,
} from '@epasu/core';
import { SCOPE_AGENT, SCOPE_INGEST } from '@epasu/core/constants';
import {
  CalendarDays,
  Download,
  Mail,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Star,
  Trash2,
  Upload,
  Zap,
} from 'lucide-react';
import { crm, uploadFiles } from '@/lib/crm-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AutomationDialog } from '@/components/AutomationDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FileEditDialog } from '@/components/FileEditDialog';
import { useQuerySeededState } from '@/lib/useQuerySeededState';
import { formatBytes } from '@/lib/utils';
import { MailAccountDialog } from '@/components/MailAccountDialog';
import { TemplateDialog } from '@/components/TemplateDialog';

export function SettingsPage() {
  return (
    <div className="space-y-6">
      <MailAccountsSection />
      <TemplatesSection />
      <LibrarySection />
      <CalendarSection />
      <IgnoreRulesSection />
      <AutomationsSection />
      <ApiKeysSection />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Anker                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Scrollt zu einem Abschnitt, wenn die Adresse seinen Anker traegt.
 *
 * React Router springt einem `#anker` nicht von selbst hinterher. Gebraucht
 * wird das von zwei Stellen, die hierher fuehren: der Rueckweg aus Googles
 * Anmeldung (`#kalender`) und die globale Suche (`#infomaterial`). Erst nach
 * dem Laden, weil sich die Hoehe der Abschnitte darueber bis dahin aendert.
 */
function useScrollZumAnker(anker: string, bereit: boolean) {
  const { hash } = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bereit && hash === `#${anker}`) {
      ref.current?.scrollIntoView({ block: 'start' });
    }
  }, [anker, bereit, hash]);
  return ref;
}

/* -------------------------------------------------------------------------- */
/*  Kalender                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Was der Ausgang des OAuth-Ausflugs fuer einen Menschen bedeutet.
 *
 * Googles eigene Meldungen sind fuer Entwickler geschrieben — „Fehler 401:
 * invalid_client“ hilft niemandem weiter, der nur seinen Kalender verbinden
 * wollte. Der Grund kommt vom Server und benennt, was zu tun ist.
 */
function kalenderErklaerung(ausgang: string, grund: string | null): string {
  switch (ausgang) {
    case 'verbunden':
      return 'Kalender verbunden.';
    case 'abgebrochen':
      return 'Die Verbindung wurde bei Google abgebrochen — es hat sich nichts geändert.';
    case 'ungueltig':
      return 'Die Rückkehr von Google war nicht gültig. Bitte noch einmal versuchen.';
    default:
      return grund
        ? `Verbindung fehlgeschlagen: ${grund}`
        : 'Verbindung fehlgeschlagen.';
  }
}

/**
 * Die Google-Kalender-Verbindung.
 *
 * Die Termine selbst stehen am Projekt und in der Kontaktakte, erkannt an den
 * Teilnehmern — hier wird nur das Konto eingerichtet. Frueher stand das auf
 * einer eigenen Kalenderseite; sie ist weg, die Verbindung bleibt, denn der
 * Vorstellungstermin ist Teil des Vertriebswegs.
 *
 * Der Ausgang der Anmeldung kommt als `?kalender=…` zurueck (siehe
 * `app-server/src/routes/calendar.ts`) und wird hier gelesen und danach aus
 * der Adresse entfernt, damit ein Neuladen die Meldung nicht wiederholt.
 */
function CalendarSection() {
  const [accounts, setAccounts] = useState<CalendarAccountView[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [hinweis, setHinweis] = useState<{ text: string; fehler: boolean } | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const ref = useScrollZumAnker('kalender', !loading);

  const ausgang = params.get('kalender');
  const grund = params.get('grund');
  useEffect(() => {
    if (!ausgang) return;
    setHinweis({
      text: kalenderErklaerung(ausgang, grund),
      fehler: ausgang !== 'verbunden',
    });
    const rest = new URLSearchParams(params);
    rest.delete('kalender');
    rest.delete('grund');
    // Den Anker behalten: `setSearchParams` verwirft ihn, und mit ihm den
    // Sprung zu diesem Abschnitt.
    const query = rest.toString();
    navigate({ pathname, search: query ? `?${query}` : '', hash }, { replace: true });
  }, [ausgang, grund, params, navigate, pathname, hash]);

  const load = useCallback(async () => {
    setAccounts(await crm.calendar.accounts());
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    setHinweis(null);
    try {
      const res = await crm.calendar.syncNow();
      setHinweis(
        res.errors.length
          ? { text: res.errors.join(' · '), fehler: true }
          : {
              text:
                res.synced > 0
                  ? `${res.synced} Termin${res.synced === 1 ? '' : 'e'} aktualisiert`
                  : 'Nichts Neues',
              fehler: false,
            },
      );
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const verbinden = () => {
    window.location.href = '/api/calendar/connect';
  };

  return (
    <div ref={ref} id="kalender" className="scroll-mt-4">
      <Card className="space-y-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-semibold">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              Kalender
            </h2>
            <p className="text-sm text-muted-foreground">
              Google Calendar. Termine erscheinen am Projekt und in der Kontaktakte —
              erkannt an den Teilnehmern.
            </p>
          </div>
          {accounts.length > 0 && (
            <Button variant="outline" disabled={syncing} onClick={() => void sync()}>
              <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Gleiche ab …' : 'Abgleichen'}
            </Button>
          )}
        </div>

        {hinweis && (
          <p
            className={
              hinweis.fehler
                ? 'rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive'
                : 'rounded-md bg-muted p-3 text-sm text-muted-foreground'
            }
          >
            {hinweis.text}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Lade …</p>
        ) : accounts.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Noch kein Kalender verbunden.</p>
            <Button onClick={verbinden}>Google Calendar verbinden</Button>
          </div>
        ) : (
          <ul className="space-y-2">
            {accounts.map((a) => (
              <li key={a.id} className="rounded-md border p-3">
                <p className="text-sm font-medium">{a.accountEmail}</p>
                <p className="text-xs text-muted-foreground">
                  {a.lastSyncAt
                    ? `zuletzt abgeglichen ${new Date(a.lastSyncAt).toLocaleString('de-DE')}`
                    : 'noch nicht abgeglichen'}
                </p>
                {a.lastError && (
                  <p className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                    {a.lastError}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Infomaterial                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Die Infomaterial-Bibliothek: Flyer, Preislisten, was an Leads rausgeht.
 *
 * Einmal hier gepflegt, aus jedem Mail-Dialog anhaengbar (`libraryOnly`) —
 * deshalb steht sie neben den Vorlagen. Zeigt bewusst nur eigene Uploads ohne
 * Bezug, keine Mail-Anhaenge und keine Projektdateien; die gehoeren zu ihrem
 * Vorgang. Die globale Suche springt mit `?q=` und `#infomaterial` hierher.
 */
function LibrarySection() {
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [summary, setSummary] = useState<{ count: number; totalBytes: number } | null>(
    null,
  );
  const [search, setSearch] = useQuerySeededState('q');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<StoredFile | null>(null);
  const [deleting, setDeleting] = useState<StoredFile | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ref = useScrollZumAnker('infomaterial', !loading);

  const load = useCallback(async () => {
    const [liste, zusammenfassung] = await Promise.all([
      crm.files.list({
        libraryOnly: true,
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
      crm.files.summary(),
    ]);
    setFiles(liste);
    setSummary(zusammenfassung);
    setLoading(false);
  }, [search]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      void load();
      return;
    }
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  const hochladen = async (auswahl: FileList | null) => {
    if (!auswahl || auswahl.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await uploadFiles(Array.from(auswahl));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div ref={ref} id="infomaterial" className="scroll-mt-4">
      <Card
        className="space-y-4 p-6"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void hochladen(e.dataTransfer.files);
        }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-semibold">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              Infomaterial
            </h2>
            <p className="text-sm text-muted-foreground">
              Dateien, die sich beim Schreiben einer Mail anhängen lassen.
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => void hochladen(e.target.files)}
          />
          <Button disabled={busy} onClick={() => inputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            {busy ? 'Lade hoch …' : 'Hochladen'}
          </Button>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Name, Beschreibung oder Schlagwort …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Infomaterial durchsuchen"
          />
        </div>

        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Lade …</p>
        ) : files.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {search.trim()
              ? 'Keine Datei passt zur Suche.'
              : 'Noch nichts abgelegt. Dateien hier hineinziehen oder oben hochladen.'}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {files.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <button
                    className="block truncate text-left text-sm font-medium hover:underline"
                    onClick={() => setEditing(f)}
                  >
                    {f.name}
                  </button>
                  <p className="truncate text-xs text-muted-foreground">
                    {formatBytes(f.sizeBytes)}
                    {f.description ? ` · ${f.description}` : ''}
                  </p>
                  {f.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {f.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <a
                  href={`/api/files/${f.id}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted"
                  aria-label={`${f.name} herunterladen`}
                >
                  <Download className="h-4 w-4" />
                </a>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${f.name} löschen`}
                  onClick={() => setDeleting(f)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {summary && summary.count > 0 && (
          <p className="text-xs text-muted-foreground">
            {summary.count} {summary.count === 1 ? 'Datei' : 'Dateien'} insgesamt ·{' '}
            {formatBytes(summary.totalBytes)} belegt (inklusive Mail-Anhänge)
          </p>
        )}

        {editing && (
          <FileEditDialog
            file={editing}
            onClose={() => setEditing(null)}
            onSaved={async () => {
              setEditing(null);
              await load();
            }}
          />
        )}

        {deleting && (
          <ConfirmDialog
            title={`„${deleting.name}“ löschen?`}
            confirmLabel="Löschen"
            destructive
            onCancel={() => setDeleting(null)}
            onConfirm={async () => {
              const res = await fetch(`/api/files/${deleting.id}`, { method: 'DELETE' });
              if (!res.ok) throw new Error('Löschen fehlgeschlagen');
              setDeleting(null);
              await load();
            }}
          >
            <p className="text-sm text-muted-foreground">
              Bereits verschickte Mails behalten ihren Anhang — der liegt beim
              Empfänger. In der Akte steht dann nur nicht mehr, welche Datei es war.
            </p>
          </ConfirmDialog>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Automations                                                               */
/* -------------------------------------------------------------------------- */

const TRIGGER_LABEL: Record<string, string> = {
  'email.received': 'Kundenmail kommt an',
  'deal.stage_changed': 'Deal wechselt die Stufe',
  'order.status_changed': 'Auftrag wechselt den Status',
};

const ACTION_LABEL: Record<string, string> = {
  create_task: 'Aufgabe anlegen',
  create_order: 'Auftrag anlegen',
  suppress_email: 'Abmelden / sperren',
  log: 'Protokolleintrag',
};

interface ActionShape {
  type?: string;
  params?: { title?: string };
}

function actionSummary(actions: unknown): string {
  if (!Array.isArray(actions)) return '—';
  return (actions as ActionShape[])
    .map((a) => {
      const label = (a?.type && ACTION_LABEL[a.type]) ?? a?.type ?? '?';
      const title = a?.params?.title;
      return title ? `${label} („${title}“)` : label;
    })
    .join(', ');
}

function conditionSummary(conditions: unknown): string | null {
  if (!conditions || typeof conditions !== 'object') return null;
  const entries = Object.entries(conditions as Record<string, unknown>);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k} = ${String(v)}`).join(', ');
}

function AutomationsSection() {
  const [rules, setRules] = useState<Automation[]>([]);
  const [editing, setEditing] = useState<Automation | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Automation | null>(null);

  const load = useCallback(async () => {
    setRules(await crm.automations.list());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(rule: Automation) {
    await crm.automations.setEnabled(rule.id, !rule.enabled);
    await load();
  }

  return (
    <Card className="space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Automations</h2>
          <p className="text-sm text-muted-foreground">
            Regeln laufen von selbst, wenn etwas passiert — eine Kundenmail
            erzeugt eine Aufgabe, ein gewonnener Deal einen Auftrag samt
            Checkliste.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus className="mr-2 h-4 w-4" />
          Neue Regel
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
          Keine Regeln angelegt.
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => {
            const cond = conditionSummary(rule.conditions);
            return (
              <div
                key={rule.id}
                className="flex items-start gap-4 rounded-lg border border-border bg-card p-4"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-muted">
                  <Zap className="h-4 w-4 text-brand" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{rule.name}</span>
                    <Badge variant={rule.enabled ? 'success' : 'neutral'}>
                      {rule.enabled ? 'aktiv' : 'aus'}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <span className="font-medium">Wenn:</span>{' '}
                    {TRIGGER_LABEL[rule.trigger] ?? rule.trigger}
                    {cond && <> ({cond})</>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Dann:</span>{' '}
                    {actionSummary(rule.actions)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {/* Enable-Umschalter */}
                  <button
                    onClick={() => toggle(rule)}
                    className={`app-no-drag relative h-5 w-9 rounded-full transition-colors ${
                      rule.enabled ? 'bg-success' : 'bg-secondary'
                    }`}
                    title={rule.enabled ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                        rule.enabled ? 'left-[18px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => setEditing(rule)}
                    className="app-no-drag text-muted-foreground hover:text-foreground"
                    title="Bearbeiten"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleting(rule)}
                    className="app-no-drag text-muted-foreground hover:text-danger"
                    title="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <AutomationDialog
          automation={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Regel „${deleting.name}“ löschen?`}
          confirmLabel="Löschen"
          destructive
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await crm.automations.delete(deleting.id);
            setDeleting(null);
            await load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Bereits angelegte Aufgaben und Aufträge bleiben bestehen. Nur die
            Regel verschwindet.
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Maschinenschlüssel                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Beschriftung für die beiden bekannten Zugänge.
 *
 * Ein Schlüssel mit einem anderen Scope-Satz — beide zugleich, keiner, oder
 * einer, den diese Oberfläche gar nicht vergibt — zeigt seine Scopes im
 * Klartext statt fälschlich als „Scraper“ durchzugehen. Diese Liste ist die
 * einzige Stelle, an der man sieht, was ein Schlüssel darf.
 */
function zweckLabel(scopes: string[]): string {
  if (scopes.length === 1 && scopes[0] === SCOPE_INGEST) return 'Scraper';
  if (scopes.length === 1 && scopes[0] === SCOPE_AGENT) return 'Claude (MCP)';
  return scopes.length > 0 ? scopes.join(', ') : 'ohne Scope';
}

/**
 * Zugänge für Programme: der Lead-Scraper (liefert nur Kontakte ein) und
 * Claude über MCP (liest und pflegt, darf aber nicht senden oder löschen).
 *
 * Der Klartext-Schlüssel wird genau einmal angezeigt, direkt nach dem Anlegen.
 * Gespeichert ist nur sein SHA-256; wer die Datenbank liest, kommt damit nicht
 * an die Schnittstelle.
 */
function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [name, setName] = useState('');
  const [zweck, setZweck] = useState<typeof SCOPE_INGEST | typeof SCOPE_AGENT>(
    SCOPE_INGEST,
  );
  const [frisch, setFrisch] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setKeys(await crm.apiKeys.list());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await crm.apiKeys.create(name.trim(), [zweck]);
      setFrisch(res.token);
      setName('');
      setZweck(SCOPE_INGEST);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const aktive = keys.filter((k) => !k.revokedAt);

  return (
    <Card className="p-6">
      <h2 className="font-semibold">Zugänge für Programme</h2>
      <p className="text-sm text-muted-foreground">
        Zwei Arten von Zugang. Ein <strong>Scraper-Schlüssel</strong> darf
        ausschließlich Kontakte einliefern und die Sperrliste lesen. Ein{' '}
        <strong>Claude-Schlüssel</strong> darf lesen und pflegen — Kontakte,
        Firmen, Deals, Aufgaben, Entwürfe. Mails versenden und Daten löschen
        kann er nicht; das ist am Server gesperrt, nicht nur in der Software,
        die ihn benutzt.
      </p>

      <form onSubmit={(e) => void anlegen(e)} className="mt-4 flex flex-wrap gap-2">
        <Input
          className="min-w-[14rem] flex-1"
          placeholder="Wofür? z.B. „Scraper Laptop“"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">Wofür</span>
          <select
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={zweck}
            onChange={(e) =>
              setZweck(e.target.value as typeof SCOPE_INGEST | typeof SCOPE_AGENT)
            }
          >
            <option value={SCOPE_INGEST}>Lead-Scraper — nur einliefern</option>
            <option value={SCOPE_AGENT}>Claude (MCP) — lesen und pflegen</option>
          </select>
        </label>
        <Button type="submit" disabled={busy}>
          Schlüssel erzeugen
        </Button>
      </form>

      {frisch && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">
            Jetzt kopieren — danach ist er nicht mehr einsehbar.
          </p>
          <code className="mt-2 block break-all rounded bg-white/70 p-2 font-mono text-xs">
            {frisch}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setFrisch(null)}
          >
            Verstanden
          </Button>
        </div>
      )}

      {aktive.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {aktive.map((k) => (
            <li
              key={k.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
            >
              <span className="min-w-0">
                {k.name}
                <code className="ml-2 font-mono text-xs text-muted-foreground">
                  {k.prefix}…
                </code>
                <span className="ml-2 text-xs text-muted-foreground">
                  {zweckLabel(k.scopes)}
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {k.lastUsedAt
                    ? `zuletzt genutzt ${new Date(k.lastUsedAt).toLocaleDateString('de-DE')}`
                    : 'noch nie genutzt'}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await crm.apiKeys.revoke(k.id);
                  await load();
                }}
              >
                Widerrufen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Postfächer                                                                */
/* -------------------------------------------------------------------------- */

function MailAccountsSection() {
  const [accounts, setAccounts] = useState<MailAccountView[]>([]);
  const [editing, setEditing] = useState<MailAccountView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<MailAccountView | null>(null);

  const load = useCallback(async () => {
    setAccounts(await crm.mailAccounts.list());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Postfächer</h2>
          <p className="text-sm text-muted-foreground">
            Aus diesen Konten wird abgerufen. Das Standardkonto versendet.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus className="mr-2 h-4 w-4" />
          Postfach
        </Button>
      </div>

      {accounts.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Noch kein Postfach eingerichtet — ohne eines bleibt der Posteingang leer.
        </p>
      ) : (
        <ul className="mt-4 space-y-2">
          {accounts.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {a.label}
                  {a.isDefault && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-normal text-primary">
                      Standard
                    </span>
                  )}
                  {!a.active && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground">
                      inaktiv
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {a.fromAddress} · IMAP {a.imap.host} · SMTP {a.smtp.host}
                </p>
                {a.lastError ? (
                  <p className="mt-1 text-xs text-destructive">{a.lastError}</p>
                ) : a.lastSyncAt ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Zuletzt abgerufen:{' '}
                    {new Date(a.lastSyncAt).toLocaleString('de-DE', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </p>
                ) : null}
              </div>

              <div className="flex gap-1">
                {!a.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Als Standard festlegen"
                    onClick={async () => {
                      await crm.mailAccounts.setDefault(a.id);
                      await load();
                    }}
                  >
                    <Star className="mr-1.5 h-4 w-4" />
                    Standard
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setEditing(a)}>
                  Bearbeiten
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${a.label} entfernen`}
                  onClick={() => setDeleting(a)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <MailAccountDialog
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Postfach „${deleting.label}“ entfernen?`}
          confirmLabel="Entfernen"
          destructive
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await crm.mailAccounts.delete(deleting.id);
            setDeleting(null);
            await load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Bereits abgerufene Nachrichten bleiben erhalten. Nur der Zugang wird
            entfernt, es wird nichts mehr abgerufen oder darüber versendet.
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Vorlagen                                                                  */
/* -------------------------------------------------------------------------- */

function TemplatesSection() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [editing, setEditing] = useState<EmailTemplate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<EmailTemplate | null>(null);

  const load = useCallback(async () => {
    setTemplates(await crm.templates.list());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">E-Mail-Vorlagen</h2>
          <p className="text-sm text-muted-foreground">
            Platzhalter: <code>{'{{anrede}}'}</code>, <code>{'{{csv_anrede}}'}</code>,{' '}
            <code>{'{{anrede_persoenlich}}'}</code>,{' '}
            <code>{'{{vorname}}'}</code>,{' '}
            <code>{'{{nachname}}'}</code>, <code>{'{{firma}}'}</code>
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus className="mr-2 h-4 w-4" />
          Vorlage
        </Button>
      </div>

      {templates.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Noch keine Vorlagen.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {templates.map((t) => (
            <li
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{t.name}</p>
                <p className="truncate text-xs text-muted-foreground">{t.subject}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditing(t)}>
                  Bearbeiten
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t.name} löschen`}
                  onClick={() => setDeleting(t)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <TemplateDialog
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Vorlage „${deleting.name}“ löschen?`}
          confirmLabel="Löschen"
          destructive
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await crm.templates.delete(deleting.id);
            setDeleting(null);
            await load();
          }}
        />
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Ignorier-Regeln                                                           */
/* -------------------------------------------------------------------------- */

function IgnoreRulesSection() {
  const [rules, setRules] = useState<MailIgnoreRule[]>([]);
  const [value, setValue] = useState('');
  const [scope, setScope] = useState<'email' | 'domain'>('email');
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRules(await crm.ignoreRules.list());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      const res = await crm.ignoreRules.add({ value, scope });
      setHinweis(
        res.hidden > 0
          ? `${res.hidden} bereits vorhandene Nachricht${res.hidden === 1 ? '' : 'en'} ausgeblendet`
          : 'Regel angelegt',
      );
      setValue('');
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="font-semibold">Nie im Posteingang</h2>
      <p className="text-sm text-muted-foreground">
        Newsletter und Automaten werden bereits an ihren Kopfzeilen erkannt. Hier
        lassen sich einzelne Absender oder ganze Domains zusätzlich ausschließen.
        Das betrifft nur die Anzeige — die Abmelde-Sperrliste ist etwas anderes.
      </p>

      <form onSubmit={(e) => void add(e)} className="mt-4 flex flex-wrap gap-2">
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={scope}
          onChange={(e) => setScope(e.target.value as 'email' | 'domain')}
          aria-label="Bereich"
        >
          <option value="email">Adresse</option>
          <option value="domain">Domain</option>
        </select>
        <Input
          className="min-w-[14rem] flex-1"
          placeholder={scope === 'email' ? 'newsletter@anbieter.de' : 'anbieter.de'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button type="submit" disabled={busy}>
          Hinzufügen
        </Button>
      </form>

      {hinweis && <p className="mt-2 text-sm text-muted-foreground">{hinweis}</p>}

      {rules.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {rules.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
            >
              <span>
                {r.value}
                <span className="ml-2 text-xs text-muted-foreground">
                  {r.scope === 'domain' ? 'ganze Domain' : 'einzelne Adresse'}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  const res = await crm.ignoreRules.remove(r.id);
                  setHinweis(
                    res.restored > 0
                      ? `${res.restored} Nachricht${res.restored === 1 ? '' : 'en'} zurückgeholt`
                      : 'Regel entfernt',
                  );
                  await load();
                }}
              >
                Entfernen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
